import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ScreenshotUploader } from '../components/ScreenshotUploader';
import { XCircle, LogOut, TrendingUp, AlertTriangle, Radio, BookOpen, Bell, Trophy, ExternalLink, MessageCircle, Send } from 'lucide-react';
import { BROKERS, CONTACTS } from '../config/brand';

const BENEFITS = [
    { Icon: Radio, title: 'Live trading signals', desc: 'Entry, SL, TP + analysis chart with every setup' },
    { Icon: Bell, title: 'Instant Telegram alerts', desc: 'Never miss a setup with real-time push' },
    { Icon: BookOpen, title: 'Phase-gated curriculum', desc: '14+ courses from beginner to advanced' },
    { Icon: Trophy, title: 'Verified track record', desc: 'TP/SL outcomes published with full R:R' },
];

export default function Rejected() {
    const { user, logout, refreshAuth } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [reason] = useState(user?.rejection_reason);
    const userBroker = (user?.broker && BROKERS[user.broker]) || BROKERS.exness;

    const handleUploadSuccess = async (data) => {
        // Refresh user to get new status from server
        if (refreshAuth) await refreshAuth();
        if (data.status === 'pending') {
            navigate('/pending-approval', { replace: true });
        }
    };

    return (
        <div className="min-h-screen p-4 py-12 relative" style={{ backgroundColor: 'var(--background-base)' }}>
            <div className="absolute top-4 right-4 z-20">
                <LanguageSwitcher />
            </div>

            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 right-1/3 w-[500px] h-[500px] rounded-full blur-3xl opacity-10" style={{ backgroundColor: 'var(--accent-danger)' }}></div>
            </div>

            <div className="relative z-10 w-full max-w-3xl mx-auto">
                <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="logo-link">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center gold-glow" style={{ backgroundColor: 'var(--primary-color)' }}>
                        <TrendingUp className="w-6 h-6" style={{ color: 'var(--primary-text)' }} />
                    </div>
                    <div>
                        <p className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>NIKAYOFX</p>
                        <p className="text-xs tracking-widest" style={{ color: 'var(--text-muted)' }}>PTG</p>
                    </div>
                </Link>

                <div className="card-surface p-8 sm:p-10" data-testid="rejected-container">
                    {/* Status header */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(255, 51, 102, 0.1)', border: '1px solid rgba(255, 51, 102, 0.3)' }}>
                            <XCircle className="w-8 h-8" style={{ color: 'var(--accent-danger)' }} />
                        </div>
                        <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-2" style={{ color: 'var(--accent-danger)' }}>
                            Account Rejected
                        </p>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="rejected-title">
                            Hi {user?.name?.split(' ')[0] || 'there'} — we couldn&apos;t verify your enrollment
                        </h1>
                        <p className="text-base leading-relaxed max-w-xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                            Your account is currently rejected because one of these requirements wasn&apos;t met:
                        </p>
                    </div>

                    {/* Reason callout */}
                    <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: 'rgba(255, 51, 102, 0.06)', border: '1px solid rgba(255, 51, 102, 0.25)' }} data-testid="rejection-reason">
                        <div className="flex gap-3">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-danger)' }} />
                            <div className="flex-1">
                                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Why your account was rejected:</p>
                                {reason ? (
                                    <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{reason}</p>
                                ) : (
                                    <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                                        <li>• You may not have registered with our affiliate link on the chosen broker</li>
                                        <li>• You may not have completed the minimum deposit</li>
                                        <li>• Your deposit screenshot may have been unclear or missing</li>
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* How to fix */}
                    <div className="mb-7">
                        <h2 className="text-base font-bold mb-3 tracking-tight" style={{ color: 'var(--text-primary)' }}>How to restore your access</h2>
                        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                            Open your Exness account using our partner link below, complete the minimum deposit, then upload your screenshot.
                        </p>

                        <div className="grid grid-cols-1 gap-3 mb-5">
                            {Object.values(BROKERS).map((b) => (
                                <a
                                    key={b.id}
                                    href={b.affiliateUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block p-4 rounded-lg transition-all hover:scale-[1.02]"
                                    style={{ backgroundColor: 'var(--background-elevated)', border: `1px solid ${b.accent}40` }}
                                    data-testid={`rejected-broker-${b.id}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{b.name}</p>
                                        <ExternalLink className="w-4 h-4" style={{ color: b.accent }} />
                                    </div>
                                    <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Min Deposit</p>
                                    <p className="text-xl font-bold font-mono" style={{ color: b.accent }}>${b.minDeposit}</p>
                                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{b.tagline}</p>
                                </a>
                            ))}
                        </div>

                        <p className="text-xs mb-4 px-3 py-2 rounded" style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--text-secondary)' }}>
                            💡 Make sure you register on <span className="font-semibold" style={{ color: userBroker.accent }}>{userBroker.name}</span> using our exact affiliate link, otherwise your account can&apos;t be verified.
                        </p>

                        {/* Screenshot uploader */}
                        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Re-upload your deposit proof:</p>
                        <ScreenshotUploader currentUrl={user?.deposit_screenshot_url} onSuccess={handleUploadSuccess} ctaLabel="Upload New Deposit Screenshot" />
                        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                            After upload, your account moves back to <span className="font-semibold" style={{ color: 'var(--primary-color)' }}>Pending</span> for re-review.
                        </p>
                    </div>

                    {/* Benefits */}
                    <div className="mb-7 p-5 rounded-xl" style={{ backgroundColor: 'var(--background-elevated)', border: '1px solid var(--border-subtle)' }}>
                        <p className="text-xs uppercase tracking-wider mb-3" style={{ color: 'var(--primary-color)' }}>What you unlock once approved</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--primary-muted)' }}>
                                        <b.Icon className="w-4 h-4" style={{ color: 'var(--primary-color)' }} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{b.title}</p>
                                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="mb-6">
                        <p className="text-sm font-semibold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>Need help? Contact us directly:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <a
                                href={CONTACTS.whatsapp.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all"
                                style={{ backgroundColor: '#25D366', color: '#FFFFFF' }}
                                data-testid="contact-whatsapp"
                            >
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp {CONTACTS.whatsapp.number}
                            </a>
                            <a
                                href={CONTACTS.telegram.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all"
                                style={{ backgroundColor: '#229ED9', color: '#FFFFFF' }}
                                data-testid="contact-telegram"
                            >
                                <Send className="w-4 h-4" />
                                Telegram {CONTACTS.telegram.handle}
                            </a>
                        </div>
                    </div>

                    <Button onClick={logout} className="btn-secondary w-full" data-testid="rejected-logout-btn">
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('common.signOut')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
